/**
 * Slice 58 — the sections of the one-page job report (presentation only).
 * Header · Photos by room · Unit items check · Maintenance · Inventory (only when low).
 */
import { useState } from "react";
import { Check, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ItemStatus, LowRow } from "@/lib/jobReport";

export const PILL = {
  missing: "bg-[#fdf1ef] text-[#b4453a]",
  broken: "bg-[#fdf3e3] text-[#8a5a00]",
  here: "bg-[#e2eeed] text-[#15776d]",
  grey: "bg-[#eef2f5] text-[#475569]",
} as const;

export function Pill({ kind, children, small }: { kind: keyof typeof PILL; children: React.ReactNode; small?: boolean }) {
  return (
    <span
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full font-bold",
        small ? "h-6 px-2.5 text-[12.5px]" : "h-7 px-3 text-[13px]",
        PILL[kind],
      )}
    >
      {children}
    </span>
  );
}

export function SectionCard({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cn("flex flex-col gap-4 rounded-[24px] bg-white p-5 max-sm:p-4", className)}>{children}</div>;
}

export function PhotoTile({
  url,
  label,
  caption,
  onOpen,
  tone,
}: {
  url: string | null;
  label: string;
  caption?: string;
  onOpen?: (url: string) => void;
  tone?: "dashed";
}) {
  return (
    <div className="flex min-w-0 flex-col gap-1.5">
      <button
        type="button"
        disabled={!url || !onOpen}
        onClick={() => url && onOpen?.(url)}
        className={cn(
          "relative aspect-[4/3] w-full overflow-hidden rounded-[12px]",
          url ? "bg-[#e8eef2]" : tone === "dashed" ? "border-2 border-dashed border-[#cfdbe2] bg-[#f8fafb]" : "bg-[#eef2f5]",
        )}
      >
        {url ? (
          <img src={url} alt="" className="h-full w-full object-cover" loading="lazy" />
        ) : (
          <span className="flex h-full items-center justify-center px-2 text-center font-mono text-[10.5px] text-[#64748b]">
            {label}
          </span>
        )}
      </button>
      {caption ? <div className="text-[12.5px] font-medium text-[#64748b]">{caption}</div> : null}
    </div>
  );
}

export function StatusButtons({
  value,
  onChange,
  small,
  disabled,
}: {
  value: ItemStatus;
  onChange: (s: ItemStatus) => void;
  small?: boolean;
  disabled?: boolean;
}) {
  const ON: Record<ItemStatus, string> = {
    here: "border-[#24a89a] bg-[#24a89a] text-white",
    missing: "border-[#b4453a] bg-[#b4453a] text-white",
    broken: "border-[#8a5a00] bg-[#8a5a00] text-white",
  };
  return (
    <div className={cn("flex shrink-0 gap-1.5", !small && "max-lg:w-full")}>
      {(["here", "missing", "broken"] as ItemStatus[]).map((s) => (
        <button
          key={s}
          type="button"
          disabled={disabled}
          onClick={() => onChange(s)}
          className={cn(
            "flex items-center justify-center rounded-[10px] border-[1.5px] font-bold transition-colors",
            small ? "h-9 px-2.5 text-[13px]" : "h-12 w-24 rounded-[14px] text-[15px] max-lg:flex-1",
            value === s ? ON[s] : "border-[#d7e0e6] bg-white text-[#334155] hover:border-[#9fb2bf]",
          )}
        >
          {s === "here" ? "Here" : s === "missing" ? "Missing" : "Broken"}
        </button>
      ))}
    </div>
  );
}

export type ExceptionRowModel = {
  id: string;
  label: string;
  where: string;
  status: ItemStatus;
  src: string;
  srcTone: "flag" | "closed" | "here" | "missing" | "broken";
  beforeUrl: string | null;
  beforeCaption: string;
  refUrl: string | null;
  refNone: boolean;
  flagUrl: string | null;
  flagCaption: string;
  note: string;
};

export function ExceptionRow({
  row,
  busy,
  onStatus,
  onNote,
  onOpenPhoto,
}: {
  row: ExceptionRowModel;
  busy: boolean;
  onStatus: (s: ItemStatus) => void;
  onNote: (v: string) => void;
  onOpenPhoto: (url: string) => void;
}) {
  const [note, setNote] = useState(row.note);
  const tint =
    row.status === "missing"
      ? "border-[#efc6c0] bg-[#fdf1ef]"
      : row.status === "broken"
        ? "border-[#e8b45c] bg-[#fdf3e3]"
        : "border-[#e6edf1] bg-white";
  const srcColor =
    row.srcTone === "flag" || row.srcTone === "broken"
      ? "text-[#8a5a00]"
      : row.srcTone === "missing"
        ? "text-[#b4453a]"
        : "text-[#15776d]";
  return (
    <div className={cn("grid gap-4 rounded-[18px] border-[1.5px] p-3.5 lg:grid-cols-[auto_minmax(0,1fr)_auto]", tint)}>
      <div className="flex gap-2.5 max-sm:grid max-sm:grid-cols-2">
        <div className="w-[138px] max-sm:w-auto">
          <PhotoTile url={row.beforeUrl} label="no before photo" caption={row.beforeCaption} onOpen={onOpenPhoto} />
        </div>
        <div className="w-[138px] max-sm:w-auto">
          <PhotoTile
            url={row.refUrl}
            label={row.refNone ? "no reference yet" : "reference"}
            caption="Reference"
            onOpen={onOpenPhoto}
            tone={row.refNone ? "dashed" : undefined}
          />
        </div>
        {row.flagUrl ? (
          <div className="w-[138px] max-sm:w-auto">
            <PhotoTile url={row.flagUrl} label="flag photo" caption={row.flagCaption} onOpen={onOpenPhoto} />
          </div>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col gap-1.5">
        <div className="text-[19px] font-bold tracking-[-0.3px] text-[#0f1720]">{row.label}</div>
        <div className="text-[14px] font-medium text-[#64748b]">{row.where}</div>
        <div className={cn("text-[13.5px] font-bold", srcColor)}>{row.src}</div>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => note !== row.note && onNote(note)}
          placeholder="Add a note"
          aria-label={`Note on ${row.label}`}
          className="mt-1 h-10 w-full max-w-[420px] rounded-[10px] border-[1.5px] border-[#e6edf1] bg-white/80 px-3 text-[14px] font-medium text-[#0f1720] outline-none focus:border-[#24a89a]"
        />
      </div>
      <StatusButtons value={row.status} onChange={onStatus} disabled={busy} />
    </div>
  );
}

export type RoomBlockModel = {
  id: string;
  name: string;
  meta: string;
  chips: { label: string; kind: "missing" | "broken" }[];
  skipText: string | null;
  groups: {
    key: string;
    name: string;
    url: string | null;
    time: string;
    itemsText: string;
    chips: { label: string; kind: "missing" | "broken" }[];
    noRef: boolean;
  }[];
  wideTitleBefore: string;
  wideBefore: { url: string; caption: string }[];
  wideAfter: { url: string; caption: string }[];
  /** Older jobs have photos only as a flat list. */
  legacyBefore: string[];
};

export function RoomBlock({
  room,
  open,
  phase,
  onToggle,
  onPhase,
  onOpenPhoto,
}: {
  room: RoomBlockModel;
  open: boolean;
  phase: "before" | "after";
  onToggle: () => void;
  onPhase: (p: "before" | "after") => void;
  onOpenPhoto: (url: string) => void;
}) {
  return (
    <div className="border-t-[1.5px] border-[#eef2f5] first:border-t-0">
      <div className="flex flex-wrap items-center justify-between gap-2.5 py-3">
        <button type="button" onClick={onToggle} className="flex min-w-0 flex-1 flex-wrap items-center gap-2.5 text-left">
          <ChevronRight className={cn("h-4 w-4 shrink-0 text-[#64748b] transition-transform", open && "rotate-90")} strokeWidth={2.4} />
          <span className="text-[17px] font-bold tracking-[-0.2px] text-[#0f1720]">{room.name}</span>
          <span className="text-[13px] font-medium text-[#8494a2]">{room.meta}</span>
          {room.chips.map((c) => (
            <Pill key={c.label} kind={c.kind} small>
              {c.label}
            </Pill>
          ))}
          {room.skipText ? <Pill kind="broken" small>{room.skipText}</Pill> : null}
        </button>
        <div className="flex rounded-full bg-[#eef2f5] p-[3px]">
          {(["before", "after"] as const).map((p) => (
            <button
              key={p}
              type="button"
              onClick={() => onPhase(p)}
              className={cn(
                "flex h-[34px] items-center rounded-full px-3.5 text-[13.5px] font-bold",
                phase === p ? "bg-white text-[#0f1720] shadow-[0_1px_3px_rgba(15,23,32,0.12)]" : "text-[#64748b]",
              )}
            >
              {p === "before" ? "Before" : "After"}
            </button>
          ))}
        </div>
      </div>
      {open ? (
        <div className="flex flex-col gap-4 pb-4 pl-6 max-sm:pl-0">
          {phase === "before" ? (
            <>
              {room.groups.length > 0 ? (
                <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                  {room.groups.map((g) => (
                    <div key={g.key} className="flex flex-col gap-2 rounded-[16px] border-[1.5px] border-[#e6edf1] p-2.5">
                      <PhotoTile url={g.url} label={`before · ${g.name.toLowerCase()}`} onOpen={onOpenPhoto} />
                      <div className="flex flex-col gap-0.5">
                        <div className="flex items-baseline justify-between gap-2">
                          <div className="truncate text-[14.5px] font-bold text-[#0f1720]">{g.name}</div>
                          <div className="shrink-0 text-[12px] font-medium text-[#8494a2]">{g.time}</div>
                        </div>
                        <div className="text-[13px] font-medium text-[#64748b]">{g.itemsText}</div>
                      </div>
                      {g.chips.length > 0 ? (
                        <div className="flex flex-wrap gap-1.5">
                          {g.chips.map((c) => (
                            <Pill key={c.label} kind={c.kind} small>
                              {c.label}
                            </Pill>
                          ))}
                        </div>
                      ) : null}
                      {g.noRef ? (
                        <div className="rounded-[10px] bg-[#fffaf0] px-2.5 py-1.5 text-[12px] font-bold leading-snug text-[#8a5a00]">
                          No reference picture for this group yet · add one in the Unit tab
                        </div>
                      ) : null}
                    </div>
                  ))}
                </div>
              ) : null}
              {room.legacyBefore.length > 0 ? (
                <div className="grid grid-cols-3 gap-2.5 md:grid-cols-4">
                  {room.legacyBefore.map((u) => (
                    <PhotoTile key={u} url={u} label="before" onOpen={onOpenPhoto} />
                  ))}
                </div>
              ) : null}
              {room.wideBefore.length > 0 ? (
                <div className="flex flex-col gap-2">
                  <div className="text-[14px] font-bold text-[#0f1720]">{room.wideTitleBefore}</div>
                  <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                    {room.wideBefore.map((w) => (
                      <PhotoTile key={w.url} url={w.url} label="before" caption={w.caption} onOpen={onOpenPhoto} />
                    ))}
                  </div>
                </div>
              ) : null}
              {room.groups.length === 0 && room.legacyBefore.length === 0 && room.wideBefore.length === 0 ? (
                <div className="text-[14px] font-medium text-[#8494a2]">No before photos for this room.</div>
              ) : null}
            </>
          ) : room.wideAfter.length > 0 ? (
            <div className="flex flex-col gap-2">
              <div className="text-[14px] font-bold text-[#0f1720]">After · wide shots of the cleaned room</div>
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {room.wideAfter.map((w) => (
                  <PhotoTile key={w.url} url={w.url} label="after" caption={w.caption} onOpen={onOpenPhoto} />
                ))}
              </div>
            </div>
          ) : (
            <div className="text-[14px] font-medium text-[#8494a2]">No after photos for this room.</div>
          )}
        </div>
      ) : null}
    </div>
  );
}

export function LowInventory({ rows, onOpenInventory }: { rows: LowRow[]; onOpenInventory?: () => void }) {
  return (
    <SectionCard>
      <div className="flex items-center justify-between gap-3">
        <div className="text-[19px] font-medium tracking-[-0.2px] text-[#0f1720]">Inventory · running low</div>
        {onOpenInventory ? (
          <button type="button" onClick={onOpenInventory} className="text-[13px] font-bold text-[#24a89a] hover:text-[#15776d]">
            Open Inventory
          </button>
        ) : null}
      </div>
      <div className="flex flex-col">
        {rows.map((l, i) => (
          <div key={l.name} className={cn("flex items-center justify-between gap-3 py-3", i > 0 && "border-t-[1.5px] border-[#eef2f5]")}>
            <div className="min-w-0">
              <div className="truncate text-[16px] font-bold text-[#0f1720]">{l.name}</div>
              <div className="text-[13.5px] font-medium text-[#64748b]">{l.level}</div>
            </div>
            <Pill kind={l.empty ? "missing" : "broken"}>{l.empty ? "Empty" : "Low"}</Pill>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

export function VerdictLine({ clear, text, maintOk }: { clear: boolean; text: string; maintOk: boolean }) {
  return (
    <div
      className={cn(
        "flex items-center gap-3 rounded-[16px] px-[18px] py-4 text-[21px] font-bold tracking-[-0.3px] max-sm:text-[18px]",
        clear ? "bg-[#e2eeed] text-[#15776d]" : maintOk ? "bg-[#fdf1ef] text-[#0f1720]" : "bg-[#fdf3e3] text-[#0f1720]",
      )}
    >
      {clear ? (
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#24a89a]">
          <Check className="h-[18px] w-[18px] text-white" strokeWidth={3.2} />
        </span>
      ) : null}
      {text}
    </div>
  );
}

/** Google Maps embed for one location stamp (no API key needed for a pin on a coordinate). */
export function MapTile({
  label,
  time,
  lat,
  lng,
  text,
  tone,
}: {
  label: string;
  time: string;
  lat: number | null;
  lng: number | null;
  text: string;
  tone: "green" | "amber" | "red";
}) {
  const has = lat != null && lng != null;
  const toneClass = tone === "red" ? "text-[#b4453a]" : tone === "amber" ? "text-[#b45309]" : "text-[#15776d]";
  return (
    <div className={cn("flex flex-col overflow-hidden rounded-[18px] border-[1.5px]", tone === "red" ? "border-[#e9c2bd]" : "border-[#e6edf1]")}>
      {has ? (
        <iframe
          title={`${label} location on Google Maps`}
          src={`https://maps.google.com/maps?q=${lat},${lng}&z=18&output=embed`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
          className="h-[210px] w-full border-0 bg-[#eef2f5]"
        />
      ) : (
        <div className="flex h-[210px] w-full items-center justify-center border-b border-dashed border-[#cdd8df] bg-[#f4f7f9] text-[14px] font-bold text-[#8494a2]">
          Location not saved
        </div>
      )}
      <div className="flex flex-wrap items-center justify-between gap-2 px-3.5 py-3">
        <div className="min-w-0">
          <div className="text-[12px] font-bold text-[#8494a2]">{label}{time ? ` · ${time}` : ""}</div>
          <div className={cn("text-[14.5px] font-bold", toneClass)}>{text}</div>
        </div>
        {has ? (
          <a
            href={`https://www.google.com/maps?q=${lat},${lng}`}
            target="_blank"
            rel="noopener noreferrer"
            className="shrink-0 text-[13px] font-bold text-[#24a89a] hover:text-[#15776d]"
          >
            Open in Google Maps ↗
          </a>
        ) : null}
      </div>
    </div>
  );
}
